import { useRecoilValue } from 'recoil';
import { navigationTranslationState } from 'store';
import Button from '@mui/material/Button';
import { Dispatch, SetStateAction, useState } from 'react';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeviceManager from '@/components/util/deviceManager';

export const FilterMenu = ({
  menuItems,
  selectedFilters,
  setSelectedFilters,
  rtl,
}: {
  menuItems: any[];
  selectedFilters: any[];
  setSelectedFilters: Dispatch<SetStateAction<number[]>>;
  rtl: boolean;
}) => {
  const translation = useRecoilValue(navigationTranslationState);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const allUniqueFilters = menuItems.reduce((acc, item) => {
    const filters = item.filters || [];
    if (!filters.length) return acc;
    filters.forEach((filter) => {
      if (!acc.find((accitem: any) => accitem.id === filter.id)) {
        acc.push(filter);
      }
    });
    return acc;
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleSelectedFilters = (id: number) => {
    setSelectedFilters((prevFilters) => {
      if (prevFilters.includes(id)) {
        return prevFilters.filter((filterId) => filterId !== id);
      }
      return [...prevFilters, id];
    });
  };

  const resetFilters = () => {
    setSelectedFilters([]);
  };

  return (
    allUniqueFilters.length > 0 && (
      <>
        <Button
          id="filter-button"
          aria-controls={open ? 'filter-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          variant="contained"
          disableElevation
          onClick={handleClick}
          sx={{
            width: { xs: '36px', lg: '99px' },
            height: { xs: '36px', lg: '44px' },
            borderRadius: { xs: '30px', sm: '8px' },
            border: open || selectedFilters.length > 0 ? '1px solid #2055FF' : '1px solid #EAECF0',
            background: open || selectedFilters.length > 0 ? 'rgba(32, 85, 255, 0.06)' : '#FFF',
            color: open || selectedFilters.length > 0 ? '#2055FF' : '#000',
            minWidth: { xs: '36px', sm: '64px' },
            direction: rtl ? 'rtl' : 'ltr',
            gap: '8px',
            '&:hover': {
              background: 'rgba(32, 85, 255, 0.06)',
              color: open || selectedFilters.length > 0 ? '#2055FF' : '#000',
            },
            '&:focus': {
              border: '1px solid #2055FF',
              background: 'rgba(32, 85, 255, 0.06)',
            },
            '.MuiButton-startIcon': {
              marginRight: { xs: 0, sm: '8px' },
              marginLeft: { xs: 0, sm: '-4px' },
            },
          }}
          startIcon={
            <i
              className="fa-solid fa-filter"
              style={{
                fontSize: '16px',
                fontWeight: 400,
                color: DeviceManager.isMobile() ? '#6B6B6B' : '#667085',
                width: '20px',
                height: '20px',
              }}
            />
          }
        >
          <Typography
            fontSize="16px"
            sx={{ display: { xs: 'none', sm: 'initial' } }}
            fontWeight={400}
          >
            {translation?.navFilter || 'Filter'}
          </Typography>
        </Button>
        <Menu
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          id="filter-menu"
          sx={{
            '.MuiPopover-paper': {
              width: '404px',
              borderRadius: '8px',
              border: '1px solid #EAECF0',
              mt: '10px',
            },
          }}
          MenuListProps={{
            'aria-labelledby': 'filter-button',
            sx: {
              background: '#fff',
              p: 0,
            },
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <Stack
            p="8px 8px 24px 24px"
            width="100%"
            direction="row"
            flexWrap="wrap"
            sx={{ direction: rtl ? 'rtl' : 'ltr' }}
          >
            <Stack direction="row" width="100%" justifyContent="space-between">
              <Typography fontSize="16px" fontWeight={400} mb="10px">
                {translation?.navFilterBy || 'Filter by'}
              </Typography>
              <Stack direction="row" gap={1}>
                <i
                  className="fa-thin fa-arrow-rotate-left"
                  onClick={resetFilters}
                  style={{ cursor: 'pointer', fontSize: '24px', width: '24px', height: '24px' }}
                />
                <i
                  className="fa-thin fa-xmark"
                  onClick={handleClose}
                  style={{ cursor: 'pointer', fontSize: '24px', width: '24px', height: '24px' }}
                />
              </Stack>
            </Stack>
            <Stack
              direction="row"
              gap={1}
              flexWrap="wrap"
              alignItems="center"
              justifyContent="flexStart"
            >
              {allUniqueFilters.map((filter) => {
                return (
                  <Button
                    variant="contained"
                    key={filter.id}
                    disableElevation
                    sx={{
                      borderRadius: '8px',
                      border: selectedFilters.includes(filter.id)
                        ? '1px solid #2055FF'
                        : '1px solid #EAECF0',
                      color: selectedFilters.includes(filter.id) ? '#2055FF' : '#344054',
                      background: selectedFilters.includes(filter.id)
                        ? 'rgba(32, 85, 255, 0.06)'
                        : '#FFF',
                      '&:hover': {
                        background: 'rgba(32, 85, 255, 0.06)',
                        color: selectedFilters.includes(filter.id) ? '#2055FF' : '#344054',
                      },
                      '&:focus': {
                        border: selectedFilters.includes(filter.id)
                          ? '1px solid #2055FF'
                          : '1px solid #EAECF0',
                      },
                    }}
                    onClick={() => handleToggleSelectedFilters(filter.id)}
                  >
                    {filter.title}
                  </Button>
                );
              })}
            </Stack>
          </Stack>
        </Menu>
      </>
    )
  );
};
